// Canonical client-side authentication failure circuit.
export interface AuthFailureCircuitOptions {
  threshold: number;
  windowMs: number;
  minimumDistinctPaths: number;
}

export interface AuthFailureCircuitState {
  open: boolean;
  justOpened: boolean;
  failures: number;
  distinctPaths: number;
}

export function shouldCaptureApiFailure(args: {
  url: string;
  status?: number;
  networkError: boolean;
}): boolean {
  if (args.url.includes('/telemetry/')) return false;
  if (args.status === 401) return false;
  return (args.status !== undefined && args.status >= 400) || args.networkError;
}

/**
 * Clear this browser's auth cookies without revoking the durable server
 * session. The failure circuit is a client-safety mechanism, not an explicit
 * user logout, so it must not destroy a session that can still be refreshed.
 */
export async function clearBrowserAuthSession(fetcher: typeof fetch = fetch): Promise<void> {
  await fetcher('/auth/clear-cookies', {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
  });
}

interface FailureSample {
  at: number;
  path: string;
}

const DEFAULT_OPTIONS: AuthFailureCircuitOptions = {
  // A legitimate permission denial or a small burst of parallel requests must
  // not sign a user out. The production incident sustained ~14 failures/sec
  // across 18 endpoints, so this opens quickly for systemic auth failure while
  // ignoring isolated 403s.
  threshold: 12,
  windowMs: 10_000,
  minimumDistinctPaths: 3,
};

export class AuthFailureCircuit {
  private readonly options: AuthFailureCircuitOptions;
  private samples: FailureSample[] = [];
  private opened = false;

  constructor(options: Partial<AuthFailureCircuitOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  record(path: string, now = Date.now()): AuthFailureCircuitState {
    const windowStart = now - this.options.windowMs;
    this.samples = this.samples.filter((sample) => sample.at >= windowStart);
    this.samples.push({ at: now, path: path.split('?', 1)[0] || 'unknown' });

    const distinctPaths = new Set(this.samples.map((sample) => sample.path)).size;
    const shouldOpen =
      this.samples.length >= this.options.threshold &&
      distinctPaths >= this.options.minimumDistinctPaths;
    const justOpened = shouldOpen && !this.opened;
    this.opened ||= shouldOpen;

    return {
      open: this.opened,
      justOpened,
      failures: this.samples.length,
      distinctPaths,
    };
  }

  reset(): void {
    this.samples = [];
    this.opened = false;
  }
}
